import { alpha, useTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { EChartsOption, MarkAreaComponentOption } from 'echarts';
import { useTranslation } from 'react-i18next';
import { formatDate, getDateFormatted } from 'shared/dates';
import { formatNumber } from '../../../../shared/utils';
import { WaterLevel, WaterLevelObservation } from '../../waterLevel.types';
import { WATER_LEVEL_LINE_COLOR } from '../../waterLevel.constants';
import {
  chartWatermarkBase64,
  chartWatermarkBase64White,
} from './chartWatermarkBase64';
import {
  CHART_AXIS_TICK_LENGTH,
  CHART_LEFT_PADDING_FOR_LABELS,
  CHART_SPLIT_LINE_INTERVAL_MS,
  CHART_Z_INDEX,
  OBSERVATION_END_BUFFER_MS,
  SCATTER_POINT_PADDING_HOURS_NO_FORECAST,
} from './useWaterLevelsChart.constants';
import { useColorScheme } from '../../../../hooks/useColorScheme';
import {
  getCategoriesMarkLineData,
  getChartBounds,
  observationToSeriesData,
} from './useWaterLevelsChart.utils';

type GetEchartsOptionsArgs = {
  observations: WaterLevelObservation[];
  forecasts?: WaterLevelObservation[];
  waterLevel?: WaterLevel;
};

const getBufferEndTime = (
  lastObservation: WaterLevelObservation | undefined,
  hasForecast: boolean,
): Date | undefined => {
  if (!lastObservation) {
    return undefined;
  }

  const scatterPointPaddingMs = hasForecast
    ? 0
    : SCATTER_POINT_PADDING_HOURS_NO_FORECAST * 60 * 60 * 1000;

  return new Date(
    lastObservation.timestamp.getTime() +
      OBSERVATION_END_BUFFER_MS +
      scatterPointPaddingMs,
  );
};

export const useWaterLevelsChart = (
  props: GetEchartsOptionsArgs,
): EChartsOption | null => {
  const { waterLevel, observations, forecasts = [] } = props;
  const { t } = useTranslation();
  const { isDarkMode } = useColorScheme();
  const theme = useTheme();

  const chartColors = isDarkMode
    ? {
        areaFill: grey[800],
        border: grey[600],
        splitLine: grey[700],
        axisPointer: grey[50],
        emphasisFill: grey[900],
      }
    : {
        areaFill: grey[100],
        border: grey[500],
        splitLine: grey[200],
        axisPointer: grey[900],
        emphasisFill: grey[50],
      };

  if (!waterLevel) {
    return null;
  }

  const echartsObservationValues = observations.map(observationToSeriesData);

  const earliestForecastDate = forecasts.at(0)?.timestamp;

  // Get the most recent observation to connect the forecast line
  const lastObservation = observations[0];
  const bufferEndTime = getBufferEndTime(lastObservation, forecasts.length > 0);

  // Prepend the last observation to the forecast data so the lines connect
  const echartsForecastValues =
    lastObservation && forecasts.length > 0
      ? [
          observationToSeriesData(lastObservation),
          ...forecasts.map(observationToSeriesData),
        ]
      : forecasts.map(observationToSeriesData);

  const { upperBounds, lowerBounds } = getChartBounds(
    [...observations, ...forecasts],
    waterLevel.floodCategories,
  );

  const floodCategoryMarkLines = getCategoriesMarkLineData({
    t,
    categoryLevels: waterLevel.floodCategories,
    unit: (waterLevel.units.level || '').toUpperCase(),
    getContrastText: theme.palette.getContrastText,
  });

  const commonSeriesValues = {
    name: t('waterLevel.weatherLevelsChart.lineLabel'),
    type: 'line' as const,
    smooth: true,
    showSymbol: false,
    symbol: 'circle',
    symbolSize: 12,
    emphasis: {
      scale: false,
      itemStyle: {
        color: chartColors.emphasisFill,
        borderColor: WATER_LEVEL_LINE_COLOR,
        borderWidth: 3,
      },
    },
    encode: {
      x: 'timestamp',
      y: 'value',
    },
    lineStyle: {
      color: WATER_LEVEL_LINE_COLOR,
      width: 3,
    },
    areaStyle: {
      color: alpha(WATER_LEVEL_LINE_COLOR, 0.2),
      origin: 'start' as const,
    },
    z: CHART_Z_INDEX.waterLevelLine,
  };

  return {
    animation: false,
    grid: {
      top: 20,
      bottom: 30,
      left: CHART_LEFT_PADDING_FOR_LABELS,
      right: 0,
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
      },
      // Prevent tooltip from covering sticky header and other elements outside of the chart
      extraCssText: 'z-index: 0',
      formatter: (params) => {
        const { value } = Array.isArray(params) ? params[0] : params;
        const [date, level] = value as [Date, number];
        const timestamp = getDateFormatted(date);
        const levelFormatted = formatNumber(level, 1);

        const keyword =
          !earliestForecastDate || date < earliestForecastDate
            ? t('waterLevel.weatherLevelsChart.observed')
            : t('waterLevel.weatherLevelsChart.forecasted');

        return `
          <div>
            <div><strong>${timestamp}</strong></div>
            <div>${keyword}: <strong>${levelFormatted} ${waterLevel.units.level}</strong></div>
          </div>
        `;
      },
    },
    dataset: [
      {
        source: echartsObservationValues,
        dimensions: ['timestamp', 'value'],
      },
      {
        source: echartsForecastValues,
        dimensions: ['timestamp', 'value'],
      },
    ],
    xAxis: {
      type: 'time',
      min: 'dataMin',
      max: !forecasts.length && bufferEndTime ? bufferEndTime : 'dataMax',
      axisLine: {
        onZero: false,
      },
      axisLabel: {
        formatter: (value) => formatDate(new Date(value), 'MMM d'),
        hideOverlap: true,
      },
      axisTick: {
        show: true,
        length: CHART_AXIS_TICK_LENGTH,
        lineStyle: {
          color: chartColors.border,
        },
      },
      axisPointer: {
        snap: true,
        lineStyle: {
          color: chartColors.axisPointer,
          width: 1,
        },
        label: {
          show: false,
        },
      },
      splitLine: {
        show: true,
        interval: CHART_SPLIT_LINE_INTERVAL_MS,
        lineStyle: {
          color: chartColors.splitLine,
        },
      },
    },
    yAxis: {
      min: lowerBounds,
      max: upperBounds,
      scale: true,
      axisLine: {
        show: false,
      },
      axisLabel: {
        margin: 12,
        formatter: (value: number) => {
          // Hide labels at min and max bounds
          if (value === lowerBounds || value === upperBounds) {
            return '';
          }
          return formatNumber(value, 1);
        },
      },
      axisTick: {
        show: true,
        length: CHART_AXIS_TICK_LENGTH,
        lineStyle: {
          color: chartColors.border,
        },
      },
      axisPointer: {
        type: 'none',
        triggerOn: 'none',
        snap: false,
        label: {
          show: false,
        },
      },
      splitLine: {
        show: false,
      },
    },
    series: [
      {
        ...commonSeriesValues,
        datasetIndex: 0,
        markLine: {
          silent: true,
          symbol: 'none',
          data: floodCategoryMarkLines.lines,
          z: CHART_Z_INDEX.floodCategoryLines,
        },
        ...(!forecasts.length &&
          lastObservation &&
          bufferEndTime && {
            markArea: {
              silent: true,
              itemStyle: {
                color: chartColors.areaFill,
              },
              z: CHART_Z_INDEX.forecastAreaFill,
              data: [
                [
                  { xAxis: lastObservation.timestamp as unknown as number },
                  { xAxis: bufferEndTime as unknown as number },
                ],
              ] as MarkAreaComponentOption['data'],
            },
          }),
      },
      {
        ...commonSeriesValues,
        datasetIndex: 1,
        lineStyle: {
          color: WATER_LEVEL_LINE_COLOR,
          width: 3,
          type: 'dashed',
        },
        ...(forecasts.length && {
          markArea: {
            silent: true,
            itemStyle: {
              color: chartColors.areaFill,
            },
            z: -1,
            data: [
              [
                { xAxis: earliestForecastDate as unknown as number },
                { xAxis: forecasts.at(-1)?.timestamp as unknown as number },
              ],
            ] as MarkAreaComponentOption['data'],
          },
        }),
      },
      ...(lastObservation
        ? [
            {
              type: 'scatter' as const,
              silent: true,
              symbol: 'circle',
              symbolSize: 12,
              itemStyle: {
                color: WATER_LEVEL_LINE_COLOR,
                opacity: 1,
              },
              data: [
                [lastObservation.timestamp.getTime(), lastObservation.level],
              ],
              z: CHART_Z_INDEX.waterLevelLine,
            },
          ]
        : []),
      // Separate series for category labels (above data lines)
      {
        type: 'line' as const,
        silent: true,
        data: [],
        markLine: {
          silent: true,
          symbol: 'none',
          data: floodCategoryMarkLines.labels,
          z: CHART_Z_INDEX.floodCategoryLabels,
        },
      },
    ],
    graphic: [
      {
        // background area behind y-axis labels
        type: 'rect',
        left: 0,
        top: 20,
        shape: {
          width: CHART_LEFT_PADDING_FOR_LABELS,
          height: 250,
        },
        style: {
          fill: chartColors.areaFill,
        },
        z: CHART_Z_INDEX.yAxisBackground,
      },
      {
        // border line at top of chart area
        type: 'line',
        left: 0,
        top: 18,
        shape: {
          x1: 0,
          y1: 0,
          x2: 2000,
          y2: 0,
        },
        style: {
          stroke: chartColors.border,
          lineWidth: 1,
        },
        z: CHART_Z_INDEX.chartBorders,
      },
      {
        // border line at bottom of chart area (above x-axis)
        type: 'line',
        left: 0,
        top: 268,
        shape: {
          x1: 0,
          y1: 0,
          x2: 2000,
          y2: 0,
        },
        style: {
          stroke: chartColors.border,
          lineWidth: 1,
        },
        z: CHART_Z_INDEX.chartBorders,
      },
      {
        type: 'image',
        style: {
          image: isDarkMode ? chartWatermarkBase64White : chartWatermarkBase64,
          width: 200,
          opacity: 0.05,
        },
        // Use fixed positioning instead of 'center' to avoid the watermark
        // jumping when chart data finishes loading. ECharts resolves 'center'
        // after the chart layout settles, causing a visible reposition.
        // 300 is the default echarts-for-react canvas height; imageHeight=20
        // (rendered height of the image at width=200) → (300 - 20) / 2 = 140
        left: 'center',
        top: 140,
        z: CHART_Z_INDEX.watermark,
      },
    ],
  };
};
